// src/games/Sudoku/api.ts
import type { iBox, iGameSudoku } from '../../interfaces/sudoku';
import { apiNode } from '../../services/api';
import { RANKING_TIPO, createRankingPosition } from '../core/api';
import type { FinishMemo } from '../core/finish/finishMemo';
import { throwIfRejected } from '../core/finish/translateFinishError';
import { finishTempo } from '../core/session/time';
import type { FinishResult } from '../core/session/useGameSession';
import {
  SudokuCell,
  buildSudokuCells,
  countSudokuCorrect,
  fillableCount,
  hydrateSudokuCells,
  scoreSudoku,
  toSudokuCache,
} from './rules';
import { SUDOKU_9, SudokuShape, findShape, parseAlphabet, shapeFromGame } from './shape';

export interface SudokuProgressRow {
  cache: unknown;
  finished: boolean | number;
  pontuacao: number | null;
  tempo: number | null;
}

export interface SaveSudokuArgs {
  userId: number;
  jogoId: number;
  cells: SudokuCell[][];
  finished: boolean;
  seconds: number;
}

export interface SudokuLoad {
  cells: SudokuCell[][];
  shape: SudokuShape;
  values: string[];
  bigSquare: iBox;
  savedSeconds: number;
  finished: FinishResult | null;
}

/** `null` quando o jogo não tem conteúdo (corpo vazio). */
export async function getSudokuGame(jogoId: number): Promise<iGameSudoku | null> {
  const { data } = await apiNode.get(`/show_sudoku/${jogoId}`);
  const game = data?.game;
  if (!game) return null;
  return typeof game === 'string' ? JSON.parse(game) : game;
}

export async function getSudokuProgress(userId: number, jogoId: number): Promise<SudokuProgressRow | null> {
  const { data } = await apiNode.get(`/sudoku_progresso/${userId}`, { params: { jogos_id: jogoId } });
  return Array.isArray(data) && data[0] ? data[0] : null;
}

export async function saveSudokuProgress({ userId, jogoId, cells, finished, seconds }: SaveSudokuArgs): Promise<{ action?: boolean }> {
  const { data } = await apiNode.post('/sudoku_progresso', {
    users_id: userId,
    jogos_id: jogoId,
    cache: toSudokuCache(cells),
    finished,
    pontuacao: scoreSudoku(cells),
    tempo: Math.max(0, Math.round(seconds)),
  });
  return data ?? {};
}

/**
 * Fim: progresso finished:true e depois o ranking tipo 8 (spec D6). Com o mesmo `memo`,
 * um reenvio pula o progresso que já foi gravado e manda só o ranking.
 */
export async function finishSudoku(args: Omit<SaveSudokuArgs, 'finished'>, memo: FinishMemo = {}): Promise<void> {
  const tempo = (memo.tempo ??= finishTempo(args.seconds));
  if (!memo.progressSaved) {
    throwIfRejected(await saveSudokuProgress({ ...args, finished: true, seconds: tempo }));
    memo.progressSaved = true;
  }
  await createRankingPosition({
    id_jogo: args.jogoId,
    id_user: args.userId,
    id_tipo: RANKING_TIPO.sudoku,
    pontuacao: scoreSudoku(args.cells),
    tempo,
  });
}

export function sudokuResult(cells: SudokuCell[][]): FinishResult {
  const total = fillableCount(cells);
  return { points: scoreSudoku(cells), maxPoints: total, correct: countSudokuCorrect(cells), total };
}

/** Abre o sudoku com o progresso salvo. Erro ao ler o progresso SOBE (não abre em branco). */
export async function loadSudokuGame(jogoId: number, userId?: number): Promise<SudokuLoad | null> {
  const [game, progress] = await Promise.all([
    getSudokuGame(jogoId),
    userId !== undefined ? getSudokuProgress(userId, jogoId) : Promise.resolve(null),
  ]);
  if (!game) return null;
  const puzzle = game['crossword-compiler']['rectangular-puzzle'];
  const gridAttributes = puzzle.sudoku.grid.attributes;
  const boxAttributes = puzzle.sudoku.attributes;
  const shape =
    shapeFromGame(game) ??
    findShape(
      Number(gridAttributes.width),
      Number(gridAttributes.height),
      Number(boxAttributes['box-width']),
      Number(boxAttributes['box-height'])
    ) ??
    SUDOKU_9;
  const base = buildSudokuCells(game, shape);
  const cells = progress ? hydrateSudokuCells(base, progress.cache) : base;

  let finished: FinishResult | null = null;
  if (progress?.finished) {
    const result = sudokuResult(cells);
    // progresso legado gravava 2 pontos por casa: mostra no máximo o total
    finished = { ...result, points: Math.min(progress.pontuacao ?? result.points, result.maxPoints) };
  }

  return {
    cells,
    shape,
    values: parseAlphabet(puzzle.attributes?.alphabet, shape),
    bigSquare: { 'box-width': String(shape.boxW), 'box-height': String(shape.boxH) },
    savedSeconds: Math.max(0, Math.round(progress?.tempo ?? 0)),
    finished,
  };
}
